import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import Sidebar from "../../components/sidebar/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import "../accounts/view/ViewAccountPage.css";
import { MUI_TABLE } from '../../utils/muiTableConfig';
import { Username } from '../../utils/privacyMask';


export default function ReferralsPage() {
  const navigate = useNavigate();

  const [referrals, setReferrals]   = useState([]);
  const [accountsMap, setAccountsMap] = useState({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [refRes, accRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/referral`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
        ]);
        setReferrals(refRes.data);
        const map = {};
        accRes.data.forEach(a => { map[a._id] = a.username; });
        setAccountsMap(map);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch referrals");
      }
    };
    fetchData();
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Referer', accessorKey: 'referer',
      Cell: ({ cell }) => <span style={{ fontWeight: 600 }}><Username value={accountsMap[cell.getValue()] || cell.getValue()} /></span>,
    },
    {
      header: 'Referred', accessorKey: 'refered',
      Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}><Username value={accountsMap[cell.getValue()] || cell.getValue()} /></span>,
    },
    {
      header: 'Date', accessorKey: 'referDate', size: 110,
      Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString(),
    },
    {
      header: 'Notes', accessorKey: 'notes',
      Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span>,
    },
  ], [accountsMap]);

  const table = useMaterialReactTable({
    columns,
    data: referrals ?? [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
    },
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem key="edit"   onClick={() => navigate(`/referral/edit/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-pencil-square" style={{ fontSize: '0.9rem', color: '#64748b' }} />Edit
      </MenuItem>,
      <MenuItem key="view"   onClick={() => navigate(`/referral/view/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-eye" style={{ fontSize: '0.9rem', color: '#64748b' }} />View
      </MenuItem>,
      <MenuItem key="delete" onClick={() => {}} sx={{ m: 0, gap: 1, color: '#f87171' }}>
        <i className="bi bi-trash" style={{ fontSize: '0.9rem', color: '#f87171' }} />Delete
      </MenuItem>,
    ],
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Referrals" />

      <div className="view-page">
        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-share-fill" style={{ color: '#a78bfa' }} />
              All Referrals
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="view-panel-count">{referrals.length}</span>
              <Link to="/referral/create" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 8, border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.08)',
                color: '#c4b5fd', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
                transition: 'background 0.18s, border-color 0.18s',
              }}>
                <i className="bi bi-plus-lg" /> New Referral
              </Link>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>
    </>
  );
}
